<template>
  <div class="rule-based app-page">
    <vue-headful title="ALICE | Rule based answer"></vue-headful>
    <div class="column-wrapper">
      <div class="single-column-left white-bg">
        <div class="column-content full-height">
          <label class="label title-label">Rule based answers</label>
          <div class="field is-grouped no-shrink">
            <div class="control is-expanded">
              <div class="field has-addons">
                <div class="control">
                  <button class="button is-static is-small">
                    <span class="icon">
                      <font-awesome-icon icon="folder"></font-awesome-icon>
                    </span>
                    <span>Rule Set</span>
                  </button>
                </div>
                <div class="control is-expanded">
                  <div class="select is-small is-fullwidth">
                    <select v-model="selectedRuleSetId" @change="reloadSetRules">
                      <option v-for="set in ruleSet" :key="set.id" :value="set.id">
                        {{set.name}}
                      </option>
                    </select>
                  </div>
                </div>
              </div>
            </div>
            <div class="control">
              <button class="button is-small is-info" @click="newRuleSet">
                <span class="icon">
                  <font-awesome-icon icon="folder-plus"></font-awesome-icon>
                </span>
                <span>Add Rule Set</span>
              </button>
            </div>
          </div>
          <div class="field no-margin">
            <table class="table is-fullwidth">
              <thead class="custom-table-head">
              <tr>
                <th>
                  <div class="field is-grouped">
                    <p class="control is-expanded has-icons-left">
                      <input
                        class="input is-small"
                        type="text"
                        placeholder="Filter rule name or keyword"
                        :disabled="!selectedRuleSetId"
                        @keyup="toggleSearchInput"
                        v-model="beforeSearchInput"
                      />
                      <span class="icon is-small is-left">
                          <font-awesome-icon icon="filter"></font-awesome-icon>
                        </span>
                    </p>
                    <div class="seperator">|</div>
                    <div class="field is-expanded has-addons">
                      <div class="control is-expanded has-icons-left">
                        <input
                          type="text"
                          class="input is-small"
                          placeholder="Add new rule"
                          v-model="newRuleTextInput"
                        />
                        <span class="icon is-small is-left">
                            <font-awesome-icon icon="file-alt"></font-awesome-icon>
                          </span>
                      </div>
                      <div class="control">
                        <button
                          class="button is-info is-small"
                          :disabled="!selectedRuleSetId"
                          @click="newRule(0,null,null)"
                        >
                          <span>Add</span>
                        </button>
                      </div>
                    </div>
                  </div>
                </th>
              </tr>
              </thead>
            </table>
          </div>
          <div class="flex-section flex-bg">
            <div class="list-wrapper">
              <virtual-list :size="42" :remain="25" :bench="50" :debounce="200" ref="ruleList">
                <div
                  v-for="rule in filteredRules"
                  @click="toggleRuleId(rule.id)"
                  class="rule-level"
                  :class="getLevelClass(rule)"
                  :key="rule.id"
                >
                  <div class="rule-inner" :class="{'selected-rule': rule.id === selectedRuleId, }">
                    <span>
                      <span class="level-icon" v-if="rule.level > 0">↳</span>
                      <span>{{rule.name}}</span>
                    </span>
                    <div class="is-pulled-right">
                      <div class="field is-grouped">
                        <div class="control" v-if="rule.level < 4">
                          <button
                            class="button is-info is-inverted is-small"
                            title="add sub-rule"
                            @click="newRuleModal(rule.level+1,rule.id, rule.origin)"
                          >
                            <span>↳</span>
                          </button>
                        </div>
                        <div class="control">
                          <button
                            class="button is-danger is-inverted is-small"
                            @click="removeRule(rule.id)"
                          >
                            <span class="icon">
                              <font-awesome-icon icon="trash"></font-awesome-icon>
                            </span>
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </virtual-list>
            </div>
          </div>
          <div class="field bottom-field">
            <div class="control">
              <div
                class="button is-danger is-inverted is-small"
                @click="deleteRuleBySetId(selectedRuleSetId)"
              >
                <span class="icon">
                  <font-awesome-icon icon="trash"></font-awesome-icon>
                </span>
                <span>Delete rule set</span>
              </div>
            </div>
            <div class="rule-set-id">Set id: {{selectedRuleSetId}}</div>
          </div>
        </div>
      </div>
      <div class="single-column-right white-bg">
        <div class="column-content full-height">
          <template v-if="selectedRule">
            <section class="head-toolbar">
              <div class="toolbar">
                <div class="toolbar-wrapper">
                  <div class="title">Rule - {{selectedRule.name}}</div>
                </div>
                <button class="button is-success has-badge-rounded has-badge-danger"
                        @click="saveData"
                        v-if="anyDirty"
                        data-badge=""
                >
                  <span class="icon">
                    <font-awesome-icon icon="save"></font-awesome-icon>
                  </span>
                  <span>Save</span>
                </button>
                <button v-else class="button is-success" @click="saveData">
                  <span class="icon">
                    <font-awesome-icon icon="save"></font-awesome-icon>
                  </span>
                  <span>Save</span>
                </button>
              </div>
            </section>
            <hr style="margin: 20px 0 20px 0" />
            <section>
              <div class="field is-horizontal">
                <div class="field-label is-small">
                  <label class="label">Keywords</label>
                </div>
                <div class="field-body">
                  <b-taginput
                    class="is-expanded keywords-input"
                    size="is-small"
                    v-model="selectedRule.keywords"
                    v-on:add="toggleKeywordDirty(true)"
                    v-on:remove="toggleKeywordDirty(true)"
                    icon="pen"
                    :confirm-key-codes="[13, 9]"
                    placeholder="Add a keyword"
                  ></b-taginput>
                </div>
              </div>
            </section>
            <hr style="margin: 15px 0 0 0" />
            <section class="flex-section answer-section">
              <!-- Tabs here -->
              <div class="toolbar">
                <div class="toolbar-wrapper">
                  <div class="tabs is-small is-boxed no-margin">
                    <ul class="no-padding no-margin">
                      <li
                        v-for="(set, index) in selectedRule.answers"
                        :class="{'is-active': selectedAnswerSetIndex === index}"
                        :key="selectedRule.id+'-answers-'+index"
                        @click="selectedAnswerSetIndex = index"
                      >
                        <a>
                          <span>Answer #{{index + 1}}</span>
                        </a>
                      </li>
                    </ul>
                  </div>
                </div>
                <button
                  class="button is-small"
                  @click="addAnswerSet"
                  :disabled="selectedRule.answers.length > 4"
                >
                  <span class="icon">
                    <font-awesome-icon icon="comments"></font-awesome-icon>
                  </span>
                  <span>Add Answer Set</span>
                </button>
              </div>
              <div class="flex-section flex-bg answers-wrapper">
                <draggable
                  class="content"
                  v-model="selectedAnswers"
                  :options="{draggable: '.answer-type-wrap', handle: '.mock-user'}"
                >
                  <div
                    class="answer-type-wrap"
                    v-for="(obj, index) in selectedAnswers"
                    :key="selectedRule.id+'-ans-'+index"
                  >
                    <any-text-answer
                      v-if="obj.type === 'send_any_message'"
                      :inputData.sync="obj"
                      :index="index"
                      :uid="selectedRule.id+'-'+index"
                      v-on:dirty="toggleAnswerDirty(true)"
                      v-on:deleted="removeAnswer(index)"
                    ></any-text-answer>
                    <change-rule
                      v-if="obj.type === 'change_rule'"
                      :inputData.sync="obj"
                      :rulesList="unfilteredRules"
                      :selectedRuleId="selectedRuleId"
                      :index="index"
                      v-on:dirty="toggleAnswerDirty(true)"
                      v-on:deleted="removeAnswer(index)"
                    ></change-rule>
                    <button-template
                      v-if="obj.type === 'button_template'"
                      :inputData.sync="obj"
                      :index="index"
                      :uid="selectedRule.id+'-'+index"
                      v-on:dirty="toggleAnswerDirty(true)"
                      v-on:deleted="removeAnswer(index)"
                    ></button-template>
                    <image-answer
                      v-if="obj.type === 'send_image'"
                      :inputData.sync="obj"
                      :index="index"
                      v-on:dirty="toggleAnswerDirty(true)"
                      v-on:deleted="removeAnswer(index)"
                    ></image-answer>
                    <video-answer
                      v-if="obj.type === 'send_video'"
                      :inputData.sync="obj"
                      :index="index"
                      v-on:dirty="toggleAnswerDirty(true)"
                      v-on:deleted="removeAnswer(index)"
                    ></video-answer>
                    <audio-answer
                      v-if="obj.type === 'send_audio'"
                      :inputData.sync="obj"
                      :index="index"
                      v-on:dirty="toggleAnswerDirty(true)"
                      v-on:deleted="removeAnswer(index)"
                    ></audio-answer>
                    <quick-reply
                      v-if="obj.type === 'quick_reply'"
                      :inputData.sync="obj"
                      :index="index"
                      v-on:dirty="toggleAnswerDirty(true)"
                      v-on:deleted="removeAnswer(index)"
                    ></quick-reply>
                  </div>
                  <div
                    class="no-answer"
                    v-if="selectedRule
                    && selectedAnswers
                    && selectedAnswers.length === 0"
                  >
                    <span class="gray-text small-text">No answers added.</span>
                  </div>
                  <div
                    class="no-answer"
                    v-if="selectedRule
                       && selectedAnswers
                       && selectedAnswers.length > 0"
                  >
                    <hr />
                    <span class="gray-text small-text">End of conversation.</span>
                  </div>
                </draggable>
              </div>
            </section>
            <section class="add-wrapper">
              <div class="field is-grouped">
                <div class="control">
                  <a
                    class="button is-danger is-inverted"
                    @click="removeAnswerSet"
                    v-if="selectedRule.answers && selectedRule.answers.length > 1"
                  >
                    <span class="icon">
                      <font-awesome-icon icon="trash"></font-awesome-icon>
                    </span>
                    <span>Delete answer set</span>
                  </a>
                </div>
                <div class="control is-expanded has-text-right">
                  <div class="dropdown is-right is-hoverable is-up has-text-left">
                    <div class="dropdown-trigger">
                      <button
                        class="button is-small"
                        aria-haspopup="true"
                        aria-controls="add-more-answer"
                      >
                        <span class="icon">
                          <font-awesome-icon icon="comment"></font-awesome-icon>
                        </span>
                        <span>Add Answer</span>
                      </button>
                    </div>
                    <div
                      class="dropdown-menu no-margin no-padding"
                      id="add-more-simple-answer"
                      role="menu"
                    >
                      <div class="dropdown-content">
                        <a
                          class="dropdown-item"
                          v-for="(type, index) in answerTypes"
                          :key="'type-' + index"
                          @click="addOutputAnswer(type.answer)"
                        >
                          <span>
                            <font-awesome-icon :icon="type.icon"></font-awesome-icon>
                          </span>
                          <span>&nbsp;&nbsp;{{type.text}}</span>
                        </a>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </section>
          </template>
        </div>
      </div>
    </div>
  </div>
</template>
<script>
import Vue from 'vue';
import { mapGetters, mapActions } from 'vuex';
import draggable from 'vuedraggable';
import virtualList from 'vue-virtual-scroll-list';
import { debounce } from 'underscore';
import alice from '../alice-api';
import anyText from '../components/Answers/AnyTextAnswer.vue';
import changeRule from '../components/Answers/ChangeRule.vue';
import buttonTemplate from '../components/Answers/ButtonTemplate.vue';
import imageAnswer from '../components/Answers/ImageAnswer.vue';
import videoAnswer from '../components/Answers/VideoAnswer.vue';
import audioAnswer from '../components/Answers/AudioAnswer.vue';
import quickReply from '../components/Answers/QuickReplyAnswer.vue';

export default {
  name: 'rule-based',
  components: {
    draggable,
    'virtual-list': virtualList,
    'any-text-answer': anyText,
    'change-rule': changeRule,
    'button-template': buttonTemplate,
    'image-answer': imageAnswer,
    'video-answer': videoAnswer,
    'audio-answer': audioAnswer,
    'quick-reply': quickReply,
  },
  data() {
    return {
      answerTypes: [
        {
          name: 'text',
          icon: 'comment-dots',
          text: 'Text answer',
          answer: {
            type: 'send_any_message',
            text: [''],
          },
        },
        {
          name: 'button_template',
          icon: 'list-alt',
          text: 'Button template',
          answer: {
            type: 'button_template',
            thumbnailImageUrl: '',
            title: '',
            text: '',
            buttons: [],
          },
        },
        {
          name: 'image',
          icon: 'image',
          text: 'Image answer',
          answer: {
            type: 'send_image',
            url: '',
          },
        },
        {
          name: 'video',
          icon: 'video',
          text: 'Video answer',
          answer: {
            type: 'send_video',
            url: '',
          },
        },
        {
          name: 'audio',
          icon: 'music',
          text: 'Audio answer',
          answer: {
            type: 'send_audio',
            url: '',
            duration: 0,
          },
        },
        {
          name: 'change_rule',
          icon: 'arrow-right',
          text: 'Redirect rule',
          answer: {
            type: 'change_rule',
            ruleId: '',
          },
        },
        {
          name: 'quick_reply',
          icon: 'comments',
          text: 'Quick reply',
          answer: {
            type: 'quick_reply',
            text: '',
            buttons: [],
          },
        },
      ],
      newRuleTextInput: '',
      beforeSearchInput: '',
      searchInput: '',
      selectedRuleId: '',
      selectedRuleSetId: null,
      selectedAnswerSetIndex: 0,
      backupRuleData: {},
      rulesList: [],
      keywordDirty: false,
      answerDirty: false,
    };
  },
  mounted() {
    this.loadRuleSet().then((list) => {
      // console.log(this.preSelectRuleSetId, this.preSelectRuleId);
      if (this.preSelectRuleSetId && this.preSelectRuleId) {
        this.selectedRuleSetId = this.preSelectRuleSetId;
        this.selectedRuleId = this.preSelectRuleId;
        this.setPreSelectRule({ ruleSetId: '', ruleId: '' });
        this.reloadSetRules(true);
      } else {
        this.setDefaultSet(list);
      }
    });
  },
  watch: {
    filteredRules() {
      this.$refs.ruleList.forceRender();
    },
  },
  methods: {
    ...mapActions({
      loadRuleSet: 'rulebase/loadRuleSet',
      createRuleSet: 'rulebase/createRuleSet',
      loadRulesBySet: 'rulebase/loadRulesBySet',
      deleteRuleSet: 'rulebase/deleteRuleSet',
      createRule: 'rulebase/createRule',
      deleteRule: 'rulebase/deleteRule',
      saveRuleAnswerFromId: 'rulebase/saveRuleAnswerFromId',
      saveRuleKeywordFromId: 'rulebase/saveRuleKeywordFromId',
      setPreSelectRule: 'persist/setPreSelectRule',
    }),
    toggleSearchInput: debounce(function () {
      // console.log('DB', this.beforeSearchInput, this.searchInput);
      this.searchInput = this.beforeSearchInput;
    }, 500),
    setDefaultSet(list) {
      if (list && list.length > 0) {
        this.selectedRuleSetId = list[0].id;
        this.reloadSetRules();
      }
    },
    toggleRuleId(ruleId) {
      if (this.anyDirty) {
        this.$buefy.dialog.confirm({
          title: 'Discard changes',
          message: 'You have unsaved changes, are you going to discard these changes?',
          cancelText: 'Cancel',
          confirmText: 'Discard',
          type: 'is-danger',
          hasIcon: true,
          onConfirm: () => {
            // Reload backup answer
            this.selectedRule = this.backupRuleData;
            this.selectedRuleId = ruleId;
            this.answerDirty = false;
            this.keywordDirty = false;
            this.selectedAnswerSetIndex = 0;
            this.$nextTick(() => {
              this.backupRuleData = JSON.parse(JSON.stringify(this.selectedRule));
            });
          },
        });
      } else {
        this.selectedRuleId = ruleId;
        this.selectedAnswerSetIndex = 0;
        this.$nextTick(() => {
          this.backupRuleData = JSON.parse(JSON.stringify(this.selectedRule));
        });
      }
    },
    reloadSetRules(doScroll) {
      this.loadRulesBySet({ setId: this.selectedRuleSetId })
        .then((result) => {
          this.rulesList = result;
          this.setDefaultRule(result, this.selectedRuleId);
          setTimeout(() => {
            if (doScroll) {
              document.querySelector('.selected-rule').scrollIntoView();
            }
          }, 200);
        })
        .catch(() => {
          this.rulesList = [];
          this.selectedRuleId = '';
        });
    },
    setDefaultRule(list, selected) {
      if (list && list.length > 0) {
        if (!selected) {
          this.selectedRuleId = list[0].id;
          this.$nextTick(() => {
            this.backupRuleData = JSON.parse(JSON.stringify(this.selectedRule));
          });
        }
      } else {
        this.selectedRuleId = '';
      }
    },
    newRuleSet() {
      this.$buefy.dialog.prompt({
        message: 'Please enter new rule set name.',
        inputAttrs: {
          placeholder: 'e.g. Set 1',
          maxlength: 25,
        },
        onConfirm: (name) => {
          this.createRuleSet({ name })
            .then(() => {
              this.$buefy.toast.open({
                type: 'is-success',
                message: 'New rule set is added',
                position: 'is-bottom',
              });
            })
            .catch(() => {
              this.$buefy.toast.open({
                type: 'is-danger',
                message: 'Unexpected error occurred.',
                position: 'is-bottom',
              });
            });
        },
      });
    },
    removeAnswerSet() {
      this.$buefy.dialog.confirm({
        title: 'Removing answer set',
        message: 'Are you sure to remove this answer set?',
        cancelText: 'Cancel',
        confirmText: 'Remove',
        type: 'is-danger',
        hasIcon: true,
        onConfirm: () => {
          this.selectedRule.answers.splice(this.selectedAnswerSetIndex, 1);
          this.selectedAnswerSetIndex = 0;
          this.toggleAnswerDirty(true);
        },
      });
    },
    newRule(level, parent, origin) {
      if (this.anyDirty) {
        this.$buefy.dialog.confirm({
          title: 'Discard changes',
          message: 'You have unsaved changes, are you going to discard these changes?',
          cancelText: 'Cancel',
          confirmText: 'Discard',
          type: 'is-danger',
          hasIcon: true,
          onConfirm: () => {
            this.newRuleFn(level, parent, origin);
          },
        });
      } else {
        this.newRuleFn(level, parent, origin);
      }
    },
    newRuleFn(level, parent, origin) {
      const name = this.newRuleTextInput.trim();
      if (name && name.length > 0) {
        const setId = this.selectedRuleSetId;
        this.createRule({
          setId,
          data: {
            name,
            parent,
            level,
            origin,
            answers: [[]],
          },
        })
          .then((rule) => {
            this.answerDirty = false;
            this.keywordDirty = false;
            this.$buefy.toast.open({
              type: 'is-success',
              message: 'New rule is added',
              position: 'is-bottom',
            });
            this.newRuleTextInput = '';
            if (rule && rule.id) {
              const { id, ruleSetId } = rule;
              this.selectedRuleId = id;
              this.selectedRuleSetId = ruleSetId;
            }
            this.reloadSetRules(true);
          })
          .catch(() => {
            this.$buefy.toast.open({
              type: 'is-danger',
              message: 'Unexpected error occurred.',
              position: 'is-bottom',
            });
          });
      } else {
        this.$buefy.toast.open({
          type: 'is-danger',
          message: 'Rule name cannot be empty.',
          position: 'is-bottom',
        });
      }
    },
    // FUture api
    // editRuleName(rule) {
    //   this.$buefy.dialog.prompt({
    //     message: 'Please enter rule name.',
    //     inputAttrs: {
    //       value: rule.name,
    //       maxlength: 50,
    //     },
    //     onConfirm: (name) => {
    //       rule.name = name;
    //       // save
    //     },
    //   });
    // },
    newRuleModal(level, parent, origin) {
      if (this.anyDirty) {
        this.$buefy.dialog.confirm({
          title: 'Discard changes',
          message: 'You have unsaved changes, are you going to discard these changes?',
          cancelText: 'Cancel',
          confirmText: 'Discard',
          type: 'is-danger',
          hasIcon: true,
          onConfirm: () => {
            this.newRuleModalFn(level, parent, origin);
          },
        });
      } else {
        this.newRuleModalFn(level, parent, origin);
      }
    },
    newRuleModalFn(level, parent, origin) {
      this.$buefy.dialog.prompt({
        message: 'Please enter new rule name.',
        inputAttrs: {
          placeholder: 'e.g. Rule 1',
          maxlength: 50,
          minlength: 1,
        },
        onConfirm: (name) => {
          const setId = this.selectedRuleSetId;
          this.createRule({
            setId,
            data: {
              name,
              parent,
              level,
              origin,
              answers: [[]],
            },
          })
            .then((rule) => {
              this.answerDirty = false;
              this.keywordDirty = false;
              this.$buefy.toast.open({
                type: 'is-success',
                message: 'New rule is added',
                position: 'is-bottom',
              });
              if (rule && rule.id) {
                const { id, ruleSetId } = rule;
                this.selectedRuleId = id;
                this.selectedRuleSetId = ruleSetId;
              }
              this.reloadSetRules(true);
            })
            .catch(() => {
              this.$buefy.toast.open({
                type: 'is-danger',
                message: 'Unexpected error occurred.',
                position: 'is-bottom',
              });
            });
        },
      });
    },
    getLevelClass(rule) {
      return `level-${rule.level}`;
    },
    addAnswerSet() {
      if (!this.selectedRule.answers) {
        this.selectedRule.answers = [[]];
      } else {
        this.selectedRule.answers.push([]);
      }
      this.toggleAnswerDirty(true);
    },
    addOutputAnswer(answer) {
      if (!this.selectedAnswers) {
        this.selectedRule.answers[this.selectedAnswerSetIndex] = [];
      }
      const ans = JSON.parse(JSON.stringify(answer));
      this.selectedAnswers.push(ans);
      // console.log(this.selectedAnswers);
      this.$forceUpdate();
      this.toggleAnswerDirty(true);
    },
    moveAnswer(obj) {
      let pos = obj.index;
      if (obj.direction === 'down') {
        pos += 1;
      } else {
        pos -= 1;
      }
      this.toggleAnswerDirty(true);
      const moving = this.selectedAnswers.splice(obj.index, 1)[0];
      this.selectedAnswers.splice(pos, 0, moving);
    },
    toggleAnswerDirty(state) {
      this.answerDirty = state;
    },
    toggleKeywordDirty(state) {
      this.keywordDirty = state;
    },
    removeAnswer(index) {
      if (this.selectedAnswers) {
        this.selectedAnswers.splice(index, 1);
        this.toggleAnswerDirty(true);
      }
    },
    saveData() {
      if (this.answerDirty && this.keywordDirty) {
        this.saveAnswer()
          .then(() => {
            this.toggleAnswerDirty(false);
            this.saveKeyword()
              .then(() => {
                this.$buefy.toast.open({
                  type: 'is-success',
                  message: 'Rule answers and keywords saved',
                  position: 'is-bottom',
                });
                this.toggleKeywordDirty(false);
              })
              .catch(() => {
                this.$buefy.toast.open({
                  type: 'is-danger',
                  message: 'Unexpected error occurred.',
                  position: 'is-bottom',
                });
              });
          })
          .catch(() => {
            this.$buefy.toast.open({
              type: 'is-danger',
              message: 'Unexpected error occurred.',
              position: 'is-bottom',
            });
          });
      } else if (this.keywordDirty) {
        this.saveKeyword()
          .then(() => {
            this.$buefy.toast.open({
              type: 'is-success',
              message: 'Rule keywords saved',
              position: 'is-bottom',
            });
            this.toggleKeywordDirty(false);
          })
          .catch(() => {
            this.$buefy.toast.open({
              type: 'is-danger',
              message: 'Unexpected error occurred.',
              position: 'is-bottom',
            });
          });
      } else if (this.answerDirty) {
        this.saveAnswer()
          .then(() => {
            this.$buefy.toast.open({
              type: 'is-success',
              message: 'Rule answers saved',
              position: 'is-bottom',
            });
            this.toggleAnswerDirty(false);
          })
          .catch(() => {
            this.$buefy.toast.open({
              type: 'is-danger',
              message: 'Unexpected error occurred.',
              position: 'is-bottom',
            });
          });
      } else {
        this.saveAnswer().then(() => {
          this.$buefy.toast.open({
            type: 'is-success',
            message: 'Rule answers saved',
            position: 'is-bottom',
          });
          this.toggleAnswerDirty(false);
        });
      }
    },
    saveAnswer(skipValidate) {
      // console.log(this.selectedRule);
      const ruleId = this.selectedRule.id;
      const data = this.selectedRule.answers; // array of answers set
      let invalidAnswer = false;
      for (let i = 0; i < data.length; i += 1) {
        // array of answers in a answer set
        const answers = data[i];
        for (let j = 0; j < answers.length; j += 1) {
          const answer = data[i][j];
          if (answer.type === 'button_template') {
            answer.buttons.forEach((_button) => {
              const button = _button;
              if (button.type === 'text_button') {
                button.uri = undefined;
              } else if (button.type === 'uri_button') {
                button.text = undefined;
              }
            });
          }
          if (!skipValidate) {
            const result = alice.validateAnswer(answer);
            if (!result.state) {
              invalidAnswer = true;
            }
          }
        }
        if (invalidAnswer) {
          break;
        }
      }
      // console.log(data);
      // If validate and got invalid answer
      if (invalidAnswer) {
        this.$buefy.toast.open({
          type: 'is-warning',
          message: 'Something wrong, please check your answers.',
          position: 'is-bottom',
        });
      } else {
        return this.saveRuleAnswerFromId({
          ruleId,
          data: this.selectedRule.answers,
        });
      }
      return false;
    },
    saveKeyword() {
      // console.log(this.selectedRule);
      const ruleId = this.selectedRule.id;
      const data = this.selectedRule.keywords;
      return this.saveRuleKeywordFromId({
        ruleId,
        data,
      })
        .then(() => {
          // console.log('Keyword added', ruleId);
        })
        .catch(() => {
          // console.error('Cannot add keyword', ruleId, e);
        });
    },
    deleteRuleBySetId(setId) {
      this.$buefy.dialog.confirm({
        title: 'Removing rule set',
        message: 'Are you sure to remove this rule set?',
        cancelText: 'Cancel',
        confirmText: 'Remove',
        type: 'is-danger',
        hasIcon: true,
        onConfirm: () => {
          this.deleteRuleSet({ setId })
            .then(() => {
              this.beforeSearchInput = '';
              this.searchInput = '';
              this.selectedRuleId = '';
              this.selectedRuleSetId = null;
              this.rulesList = [];
              this.loadRuleSet().then((list) => {
                this.setDefaultSet(list);
              });
              this.$buefy.toast.open({
                type: 'is-success',
                message: 'Rule set removed.',
                position: 'is-bottom',
              });
            })
            .catch(() => {
              this.$buefy.toast.open({
                type: 'is-danger',
                message: 'Unexpected error occurred.',
                position: 'is-bottom',
              });
            });
        },
      });
    },
    removeRule(ruleId) {
      this.$buefy.dialog.confirm({
        title: 'Removing rule',
        message: 'Are you sure to remove this rule?',
        cancelText: 'Cancel',
        confirmText: 'Remove',
        type: 'is-danger',
        hasIcon: true,
        onConfirm: () => {
          this.deleteRule({ ruleId })
            .then(() => {
              // this.searchInput = '';
              this.selectedRuleId = '';
              this.reloadSetRules();
              this.$buefy.toast.open({
                type: 'is-success',
                message: 'Rule removed.',
                position: 'is-bottom',
              });
            })
            .catch(() => {
              this.$buefy.toast.open({
                type: 'is-danger',
                message: 'Unexpected error occurred.',
                position: 'is-bottom',
              });
            });
        },
      });
    },
  },
  computed: {
    ...mapGetters({
      ruleSet: 'rulebase/ruleSet',
      preSelectRuleSetId: 'persist/preSelectRuleSetId',
      preSelectRuleId: 'persist/preSelectRuleId',
    }),
    unfilteredRules() {
      if (this.rulesList) {
        // Sort rule by level
        const levelData = [];
        const sortedList = [];
        for (let i = 0; i < this.rulesList.length; i += 1) {
          const { level } = this.rulesList[i];
          if (levelData[level]) {
            levelData[level].push(this.rulesList[i]);
          } else {
            levelData[level] = [this.rulesList[i]];
          }
        }
        // Sort each by text : Level 0 sort by asc, Others sort by desc
        for (let j = 0; j < levelData.length; j += 1) {
          const levelArr = levelData[j];
          if (j === 0) {
            levelArr.sort((a, b) => {
              if (a.name < b.name) {
                return -1;
              }
              if (a.name > b.name) {
                return 1;
              }
              return 0;
            });
          } else {
            levelArr.sort((a, b) => {
              if (b.name < a.name) {
                return -1;
              }
              if (b.name > a.name) {
                return 1;
              }
              return 0;
            });
          }
        }
        // Outer level is pushed first
        for (let j = 0; j < levelData.length; j += 1) {
          for (let k = 0; k < levelData[j].length; k += 1) {
            if (j === 0) {
              levelData[j][k].path = levelData[j][k].id;
              sortedList.push(levelData[j][k]);
            } else {
              // Find the parent in sortedList
              let targetIndex = -1;
              let parentObj = {};
              for (let l = 0; l < sortedList.length; l += 1) {
                if (sortedList[l].id === levelData[j][k].parent) {
                  targetIndex = l;
                  parentObj = sortedList[l];
                  break;
                }
              }
              if (targetIndex > -1) {
                levelData[j][k].path = `${parentObj.path}|${levelData[j][k].id}`;
                if (targetIndex === sortedList.length - 1) {
                  // Last mean push new one.
                  sortedList.push(levelData[j][k]);
                } else {
                  // console.log(sortedList[targetIndex+1]);
                  sortedList.splice(targetIndex + 1, 0, levelData[j][k]);
                  // console.log(sortedList[targetIndex+1]);
                }
              } else {
                // console.log('This should not happen - no parent', levelData[j][k].id);
                sortedList.push(levelData[j][k]);
              }
            }
          }
        }
        // console.log(sortedList);
        return sortedList;
      }
      return [];
    },
    rulesOriginMap() {
      const result = {};
      if (this.rulesList) {
        for (let i = 0; i < this.rulesList.length; i += 1) {
          const rule = this.rulesList[i];
          if (result[rule.origin]) {
            result[rule.origin].push(rule);
          } else {
            result[rule.origin] = [rule];
          }
        }
        Object.keys(result).forEach((k) => {
          if (result[k] && result[k].length > 1) {
            result[k] = result[k].sort((a, b) => a.level - b.level);
          }
        });
      }
      return result;
    },
    filteredRules() {
      // TODO refactor this or unfilteredRules
      const searchInput = this.searchInput.toLowerCase();
      if (searchInput.length > 0 && this.rulesList) {
        const unExistRulesPath = this.rulesList.filter((it) => it.path === null || it.path === undefined || it.path.toString().trim().length <= 0);
        let cloneRulesList;
        if (unExistRulesPath.length > 0) {
          cloneRulesList = this.unfilteredRules;
        } else {
          cloneRulesList = this.rulesList;
        }
        // filter by text and keyword
        const rawFilter = cloneRulesList.filter((data) => {
          const longKeywords = data.keywords.join('|').toLowerCase();
          const target = data.name.toLowerCase();
          return target.indexOf(searchInput) > -1 || longKeywords.indexOf(searchInput) > -1;
        });
        const rawFilteredPathList = rawFilter.map((obj) => obj.path);
        // get map of path
        const filteredIdMap = new Map();
        rawFilteredPathList.forEach((path) => {
          if (path.indexOf('|') > -1) {
            const splitted = path.split('|');
            splitted.forEach((subPath) => {
              filteredIdMap.set(subPath, {});
            });
          } else {
            filteredIdMap.set(path, {});
          }
        });
        // get all possible child of filtered
        // console.log(rawFilter.length);
        for (let i = 0; i < rawFilter.length; i += 1) {
          const obj = rawFilter[i];
          const { origin } = obj;
          if (this.rulesOriginMap && this.rulesOriginMap[origin] && this.rulesOriginMap[origin].length > 1) {
            const list = this.rulesOriginMap[origin];
            for (let j = 0; j < list.length; j += 1) {
              if (list[j].level > obj.level) {
                // FIXME this is bug -- find its valid parent before set the child
                // FIXME: 100 > gg | 101 > ff -- search for 100 will got ff too which is wrong
                filteredIdMap.set(list[j].id, {});
              }
            }
          }
        }
        // then search from id
        const filteredIdList = [...filteredIdMap.keys()];
        let filteredRules = [];
        // console.log(filteredIdList);
        filteredRules = cloneRulesList.filter((rule) => filteredIdList.indexOf(rule.id) > -1);
        // Sort rule by level
        const levelData = [];
        const sortedList = [];
        for (let i = 0; i < filteredRules.length; i += 1) {
          const { level } = filteredRules[i];
          if (levelData[level]) {
            levelData[level].push(filteredRules[i]);
          } else {
            levelData[level] = [filteredRules[i]];
          }
        }
        // Sort each by text : Level 0 sort by asc, Others sort by desc
        for (let j = 0; j < levelData.length; j += 1) {
          const levelArr = levelData[j];
          if (j === 0) {
            levelArr.sort((a, b) => {
              if (a.name < b.name) {
                return -1;
              }
              if (a.name > b.name) {
                return 1;
              }
              return 0;
            });
          } else {
            levelArr.sort((a, b) => {
              if (b.name < a.name) {
                return -1;
              }
              if (b.name > a.name) {
                return 1;
              }
              return 0;
            });
          }
        }
        // Outer level is pushed first
        for (let j = 0; j < levelData.length; j += 1) {
          for (let k = 0; k < levelData[j].length; k += 1) {
            if (j === 0) {
              levelData[j][k].path = levelData[j][k].id;
              sortedList.push(levelData[j][k]);
            } else {
              // Find the parent in sortedList
              let targetIndex = -1;
              let parentObj = {};
              for (let l = 0; l < sortedList.length; l += 1) {
                if (sortedList[l].id === levelData[j][k].parent) {
                  targetIndex = parseInt(l, 10);
                  parentObj = sortedList[l];
                  break;
                }
              }
              if (targetIndex > -1) {
                levelData[j][k].path = `${parentObj.path}|${levelData[j][k].id}`;
                if (targetIndex === sortedList.length - 1) {
                  // Last mean push new one.
                  sortedList.push(levelData[j][k]);
                } else {
                  sortedList.splice(targetIndex + 1, 0, levelData[j][k]);
                }
              } else {
                // console.log('This should not happen - no parent', levelData[j][k].id);
                sortedList.push(levelData[j][k]);
              }
            }
          }
        }
        return sortedList;
      }
      return this.unfilteredRules;
    },
    selectedRule: {
      // FIXME this shouldn't use computed
      get() {
        if (this.selectedRuleId) {
          const result = this.rulesList.find((rule) => rule.id === this.selectedRuleId);
          if (result) {
            return result;
          }
        }
        return null;
      },
      set(data) {
        if (this.selectedRuleId) {
          const index = this.rulesList.map((rule) => rule.id).indexOf(this.selectedRuleId);
          if (index > -1) {
            this.rulesList[index] = data;
          }
        }
      },
    },
    selectedAnswers: {
      get() {
        if (this.selectedRule) {
          return this.selectedRule.answers[this.selectedAnswerSetIndex];
        }
        return false;
      },
      set(val) {
        if (this.selectedRuleId) {
          const index = this.rulesList.map((rule) => rule.id).indexOf(this.selectedRuleId);
          if (index > -1) {
            Vue.set(this.rulesList[index].answers, this.selectedAnswerSetIndex, val);
            this.answerDirty = true;
          }
          // console.log(index, this.rulesList[index].answers);
        }
      },
    },
    anyDirty() {
      return this.keywordDirty || this.answerDirty;
    },
  },
};
</script>
<style lang="scss" scoped>
  .rule-based {
    text-align: left;
    .title-label {
      font-size: 1.25rem;
      line-height: 2.5rem;
    }
    .column-content {
      padding: 0.6rem;
    }
    .flex-section {
      display: flex;
      flex: 1 1 auto;
      overflow: auto;
      &.flex-bg {
        background-color: #f9f9f9;
        box-shadow: inset 0 1px 2px rgba(0, 0, 0, 0.1);
      }
    }
    .answer-section {
      margin-top: 15px;
      flex-direction: column;
    }
    .list-wrapper {
      display: flex;
      flex-direction: column;
      overflow-x: hidden;
      width: 100%;
    }
    .table {
      border: none;
      td {
        border-width: 1px;
        border-color: #ddd;
        cursor: pointer;
      }
    }
    .custom-table-head {
      border: 1px solid #ddd;
      background-color: #f6f6f6;
      .seperator {
        color: #ddd;
        margin-right: 10px;
      }
    }
    .selected-rule {
      color: #fff;
      font-weight: bold;
      background-color: #0d94e4;
      border-color: #0d94e4 !important;
      /*background-image: linear-gradient(to right,#32a8ee, #0d94e4);*/
      .level-icon {
        color: #fff !important;
      }
    }
    .rule-level {
      line-height: 2em;
      min-height: 42px;
      background-color: #fff;
      border-bottom: 1px solid #ddd;
      .icon {
        font-weight: bold;
        vertical-align: middle;
      }
    }
    .rule-inner {
      min-height: 42px;
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 0 .5rem;
    }
    .level-1 {
      border-left: 20px solid #ddd;
      .level-icon {
        margin-right: 5px;
        color: #ff3d3a;
      }
    }
    .level-2 {
      border-left: 40px solid #ddd;
      .level-icon {
        color: #ff823a;
      }
    }
    .level-3 {
      border-left: 60px solid #ddd;
      .level-icon {
        color: #46ab36;
      }
    }
    .level-4 {
      border-left: 80px solid #ddd;
      .level-icon {
        color: #677bff;
      }
    }
    .toolbar {
      display: flex;
      flex: 0 0 auto;
      flex-direction: row;
      .toolbar-wrapper {
        display: flex;
        flex: 1 1 auto;
        overflow-x: scroll;
        .title {
          text-overflow: ellipsis;
          white-space: nowrap;
          overflow: hidden;
          font-size: 1.25rem;
          line-height: 2.25rem;
        }
      }
      .button {
        margin-left: 10px;
      }
    }
    .answers-wrapper {
      flex-direction: column;
      padding: 20px;
      border-top: 1px solid #ededed;
      .no-answer {
        text-align: center;
        padding: 10px;
      }
      .answer-type-wrap {
        margin-bottom: 15px;
      }
    }
    .add-wrapper {
      background-color: #f9f9f9;
      padding: 10px;
      border-top: 1px solid #eee;
      .dropdown {
        .button {
          border-radius: 3px;
        }
      }
      .button {
        border-radius: 3px;
        margin-right: 5px;
      }
    }
    .gray-text {
      color: #aaa;
    }
    .small-text {
      font-size: 0.75rem;
    }
    .bottom-field {
      position: relative;
      .control {
        border-top: 2px solid #eee;
      }
    }
    .rule-set-id {
      position: absolute;
      top: 6px;
      right: 0;
      color: #ddd;
      font-size: 12px;
    }
  }
</style>
